import { Classes, Colors, Dialog, Intent, ButtonGroup } from "@blueprintjs/core";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { CompetitionResultApi, CompetitionResultGridDto, CompetitionResultSearch } from "../../../../api";
import { AddButton, CustomDataTable, DeleteButton, EditButton, ExportButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useDeleteApi, useSearchApiWithLanguageRefresh, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { CompetitionResultPanel } from "./CompetitionResultPanel";

export interface ICompetitionResultListProps {
  competitionId: number;
  competitionRequestId: number;
  editMode: boolean;
  hideExportButton?: boolean;
}

export const CompetitionResultList: React.FunctionComponent<ICompetitionResultListProps> = ({
  competitionId,
  editMode,
  competitionRequestId,
  hideExportButton = false,
}) => {
  const { t } = useTl();
  const api = useApiService(CompetitionResultApi);
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [competitionResultId, setCompetitonResultId] = React.useState(null);
  const [sObjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
  });

  const searchFunction = React.useCallback(
    (sObj?) => {
      const sobj = CompetitionResultSearch.fromJS({
        ...sObj,
        competitionId: competitionId,
        languageId: currentLanguage?.languageId,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, competitionId, currentLanguage?.languageId]
  );

  const { search, loading } = useSearchApiWithLanguageRefresh({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { deleteItem } = useDeleteApi(
    React.useMemo(
      () => ({
        deleteApiFn: (data: number) => api.delete(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const addItemFunction = React.useCallback((compResultId: any) => {
    setCompetitonResultId(compResultId);
    setDialogOpened(true);
  }, []);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: CompetitionResultGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.competitionResultId)} />
            <EditButton minimal={true} onClick={() => addItemFunction(row.competitionResultId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Dog),
        fieldName: "dog",
        render: (row: CompetitionResultGridDto) => (
          <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
            {row.dog}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Judge),
        fieldName: "judge",
      },
      {
        header: () => t(ETLCodes.CatalogNumber),
        fieldName: "catalogNumber",
      },
      {
        header: () => t(ETLCodes.Qualification),
        fieldName: "qualification",
      },
      {
        header: () => t(ETLCodes.Class),
        fieldName: "class",
      },
      {
        header: () => t(ETLCodes.Result),
        fieldName: "result",
      },
    ],
    [addItemFunction, deleteItem, editMode, t]
  );

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Results)}
        rightElement={
          <ButtonGroup>
            {hideExportButton ? null : (
              <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
            )}
            {editMode && (
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction(0);
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.Result)}
        isOpen={dialogOpened}
        onClose={() => {
          setDialogOpened(false);
          search();
        }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1500, height: 700, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <CompetitionResultPanel
            onSaved={() => {
              search();
              setDialogOpened(false);
            }}
            search={search}
            competitionId={competitionId}
            competitionResultId={competitionResultId}
            competitionRequestId={competitionRequestId}
          />
        </div>
      </Dialog>
    </>
  );
};
